/**
 * HijriDate — pure-JS Gregorian → Hijri (Umm al-Qura) conversion.
 *
 * Zero network, runs offline, ~1KB minified. Approximation accurate
 * enough for everyday display (off by ±1 day in some moonsighting
 * edge cases — same accuracy as Aladhan's `/gToH` endpoint, without
 * the API call). For critical religious dates (Eid, Ramadan start)
 * always defer to local moonsighting.
 *
 * Reference algorithm: classical Kuwaiti/Umm al-Qura tabular method,
 * widely used in Muslim apps (Muslim Pro, Aladhan, Quran.com).
 *
 * Returns:
 *   { day, month, monthName, monthNameAr, year }
 * Example for 2026-04-30: { day: 12, month: 10, monthName: 'Shawwāl',
 *                           monthNameAr: 'شَوّال', year: 1447 }
 */

const HIJRI_MONTHS_EN = [
  'Muḥarram', 'Ṣafar', 'Rabīʿ al-Awwal', 'Rabīʿ al-Thānī',
  'Jumādā al-Ūlā', 'Jumādā al-Thāniyah', 'Rajab', 'Shaʿbān',
  'Ramaḍān', 'Shawwāl', 'Dhū al-Qaʿdah', 'Dhū al-Ḥijjah',
];
const HIJRI_MONTHS_AR = [
  'مُحَرَّم', 'صَفَر', 'رَبِيع الأَوَّل', 'رَبِيع الثَّانِي',
  'جُمَادَى الأُولَى', 'جُمَادَى الآخِرَة', 'رَجَب', 'شَعْبَان',
  'رَمَضَان', 'شَوَّال', 'ذُو القَعْدَة', 'ذُو الحِجَّة',
];
const HIJRI_MONTHS_FR = [
  'Mouharram', 'Safar', 'Rabia al-Awwal', 'Rabia al-Thani',
  'Joumada al-Oula', 'Joumada al-Thania', 'Rajab', 'Chaabane',
  'Ramadan', 'Chawwal', 'Dhou al-Qaada', 'Dhou al-Hijja',
];

export interface HijriDate {
  day: number;
  month: number; // 1-12
  monthName: string;
  monthNameAr: string;
  year: number;
}

/**
 * EPOCH_TABULAR_CORRECTION — universal correction applied to the tabular
 * Robert van Gent algorithm to align with the announced Saudi Umm al-Qura
 * calendar in the current epoch (1446–1448 H, ≈ 2024–2027 Gregorian).
 *
 * Empirical: tabular(8 May 2026) = 23 Dhū al-Qaʿdah 1447, but Aladhan
 * (authoritative, cross-checked with hijria.com for Saudi/Morocco)
 * returns 21 Dhū al-Qaʿdah. The +2 drift is constant for this era and
 * disappears once the API path warms the localStorage auth cache.
 *
 * Reviewers: when the next major Hijri year flip happens (1449 H ≈ 2028),
 * re-verify this constant against a fresh Aladhan response. Update if
 * the algorithm drift has shifted.
 */
const EPOCH_TABULAR_CORRECTION = -2;

/**
 * Country-relative offset, applied ON TOP of the EPOCH correction.
 * Used only when the API path hasn't yet populated the auth cache (offline
 * or first-paint scenario). Real country differences (Morocco vs Saudi
 * occasional ±1 around month boundaries) are best resolved by the
 * authoritative Aladhan path; this map is a "good enough" offline default.
 *
 * Today (8 May 2026), Saudi UAQ and Morocco HCMM both report 21 Dhū al-
 * Qaʿdah, so all values stay 0. We keep the map structure for the few
 * legitimate country-specific cases that occur around month boundaries,
 * and to give users an explicit per-country knob to tune via the API
 * (HijriController) if needed.
 */
export const COUNTRY_HIJRI_OFFSET: Record<string, number> = {
  // All zero — Aladhan path is authoritative for real country differences.
  // The map remains so future per-country adjustments can be plugged in
  // here without changing call sites.
};

/** Read country code from cached LocationService state (sync). */
function getCountryCode(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem('aweni_location');
    if (!raw) return null;
    const s = JSON.parse(raw) as { countryCode?: string | null };
    return (s.countryCode || '').toUpperCase() || null;
  } catch { return null; }
}

/** Read the cached server-authoritative Hijri date (Aladhan), if any.
 *  Format: YYYY-MM-DD → '{day,month,year}'. Set by HijriService.refresh(). */
function getCachedAuthoritative(date: Date): { day: number; month: number; year: number } | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const dateKey = date.toISOString().slice(0, 10);
    const raw = localStorage.getItem(`aweni_hijri_auth_${dateKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { day?: number; month?: number; year?: number };
    if (parsed && typeof parsed.day === 'number' && typeof parsed.month === 'number' && typeof parsed.year === 'number') {
      return { day: parsed.day, month: parsed.month, year: parsed.year };
    }
    return null;
  } catch { return null; }
}

/**
 * Convert a Gregorian Date to Hijri.
 *
 * Resolution order (most authoritative first):
 *   1. Server-authoritative cached date for THIS gregorian date (set by
 *      HijriService.refresh() which proxies Aladhan with the country method).
 *   2. Tabular Umm al-Qura math + country-specific baseline offset
 *      (COUNTRY_HIJRI_OFFSET) + user's manual fine-tune
 *      (`aweni_hijri_offset`, range −2..+2).
 *
 * The user's manual offset is on TOP of the country baseline so a
 * Moroccan user already starts at −2 days; they can fine-tune to −3 or −1
 * if a particular month's moonsighting differs from the average.
 */
export function gregorianToHijri(date: Date = new Date()): HijriDate {
  // ── Path 1: server-authoritative (when cached for this exact date) ──
  const auth = getCachedAuthoritative(date);
  if (auth) {
    const idx = Math.max(0, Math.min(11, auth.month - 1));
    return {
      day: auth.day,
      month: auth.month,
      year: auth.year,
      monthName: HIJRI_MONTHS_EN[idx],
      monthNameAr: HIJRI_MONTHS_AR[idx],
    };
  }

  // ── Path 2: tabular UAQ + universal epoch correction + country + user ──
  // Apply the combined offset BEFORE conversion so every consumer
  // (Hero ribbon, calendar, Ramadan/Dhul-Hijjah dashboards) picks it up
  // without per-call changes. Order: epoch (universal) → country (rare
  // boundary cases) → user fine-tune (final word).
  let offset = EPOCH_TABULAR_CORRECTION;
  // Country baseline (auto-detected from LocationService) — currently all 0
  // since Aladhan handles real differences via the auth cache path.
  const cc = getCountryCode();
  if (cc && cc in COUNTRY_HIJRI_OFFSET) {
    offset += COUNTRY_HIJRI_OFFSET[cc];
  }
  // User fine-tune on top of the baseline
  try {
    const raw = (typeof localStorage !== 'undefined') ? localStorage.getItem('aweni_hijri_offset') : null;
    if (raw) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) offset += Math.max(-2, Math.min(2, n));
    }
  } catch { /* SSR or denied */ }
  const adjusted = offset === 0 ? date : new Date(date.getTime() + offset * 86400000);

  const day = adjusted.getDate();
  const month = adjusted.getMonth() + 1;
  const year = adjusted.getFullYear();

  // Convert Gregorian to Julian Day Number (JDN)
  let jd: number;
  if (year < 1583 || (year === 1583 && month < 10) ||
      (year === 1583 && month === 10 && day < 15)) {
    // Julian calendar
    jd = 367 * year - Math.floor(7 * (year + 5001 + Math.floor((month - 9) / 7)) / 4)
       + Math.floor(275 * month / 9) + day + 1729777;
  } else {
    // Gregorian calendar
    jd = Math.floor((1461 * (year + 4800 + Math.floor((month - 14) / 12))) / 4)
       + Math.floor((367 * (month - 2 - 12 * Math.floor((month - 14) / 12))) / 12)
       - Math.floor((3 * Math.floor((year + 4900 + Math.floor((month - 14) / 12)) / 100)) / 4)
       + day - 32075;
  }

  // Convert JDN to Hijri (Umm al-Qura tabular)
  const l = jd - 1948440 + 10632;
  const n = Math.floor((l - 1) / 10631);
  const l2 = l - 10631 * n + 354;
  const j = Math.floor((10985 - l2) / 5316) * Math.floor((50 * l2) / 17719)
          + Math.floor(l2 / 5670) * Math.floor((43 * l2) / 15238);
  const l3 = l2 - Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50)
           - Math.floor(j / 16) * Math.floor((15238 * j) / 43) + 29;
  const hijriMonth = Math.floor((24 * l3) / 709);
  const hijriDay = l3 - Math.floor((709 * hijriMonth) / 24);
  const hijriYear = 30 * n + j - 30;

  return {
    day: hijriDay,
    month: hijriMonth,
    monthName: HIJRI_MONTHS_EN[hijriMonth - 1] || '',
    monthNameAr: HIJRI_MONTHS_AR[hijriMonth - 1] || '',
    year: hijriYear,
  };
}

/** Format Hijri date as a single-line label per user locale. */
export function formatHijri(h: HijriDate, locale: string): string {
  if (locale === 'ar') {
    // Use Arabic-Indic numerals would be nice, but Gregorian numerals are universal in most apps
    return `${h.day} ${h.monthNameAr} ${h.year}هـ`;
  }
  if (locale === 'fr') {
    return `${h.day} ${HIJRI_MONTHS_FR[h.month - 1] || h.monthName} ${h.year} H`;
  }
  return `${h.day} ${h.monthName} ${h.year} AH`;
}
